import { useEffect, useState } from 'react';
import { Allocation } from './allocations';

type Seed = { clientId: string; amounts: Record<string, number | null> };

/**
 * Who a cashflow item applies to and each member's own figures, and why a save refused them,
 * which stands until the form is opened again or the members or their figures change.
 */
export function useAllocations(
  opened: boolean,
  seed: Seed[] | undefined,
  defaultClientId?: string,
) {
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [refusal, setRefusal] = useState<string | undefined>();

  useEffect(() => {
    if (!opened) return;
    setRefusal(undefined);
    if (seed && seed.length > 0) {
      // An amount not recorded opens as an empty field, not as zero
      setAllocations(
        seed.map(entry => ({
          ...entry,
          amounts: Object.fromEntries(
            Object.entries(entry.amounts).map(([key, value]) => [key, value ?? '']),
          ),
        })),
      );
      return;
    }
    setAllocations(defaultClientId ? [{ clientId: defaultClientId, amounts: {} }] : []);
    // `seed` is rebuilt on every render by its caller, so it cannot be a dependency without
    // resetting the form on each keystroke; opening the modal is what should reset it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, defaultClientId]);

  const edit = (change: (current: Allocation[]) => Allocation[]) => {
    setRefusal(undefined);
    setAllocations(change);
  };

  const addMember = (clientId: string) => edit(current => [...current, { clientId, amounts: {} }]);

  const removeMember = (clientId: string) =>
    edit(current => current.filter(allocation => allocation.clientId !== clientId));

  const setAmount = (clientId: string, key: string, value: number | '') =>
    edit(current =>
      current.map(allocation =>
        allocation.clientId === clientId
          ? { ...allocation, amounts: { ...allocation.amounts, [key]: value } }
          : allocation,
      ),
    );

  return { allocations, addMember, removeMember, setAmount, refusal, setRefusal };
}
